import { formatIsoDate, subtractDay } from '@src/shared/lib';
import { useEffect, useRef } from 'react';
import type { TimelineSlot, VisibleRange } from '../../types';
import type { CalendarController } from './timeline';

type UseTimelineSelectionSyncParams = {
  activeSelection: TimelineSlot | null;
  calendar: Pick<CalendarController, 'select' | 'unselect'>;
  isDayView: boolean;
  visibleRange: VisibleRange;
};

export default function useTimelineSelectionSync({
  activeSelection,
  calendar,
  isDayView,
  visibleRange,
}: UseTimelineSelectionSyncParams) {
  const syncedSelectionRef = useRef<string | null>(null);
  const isSyncingSelectionRef = useRef(false);

  useEffect(() => {
    if (
      !activeSelection?.start ||
      !activeSelection.end ||
      !activeSelection.resourceId
    ) {
      if (syncedSelectionRef.current !== null) {
        calendar.unselect();
        syncedSelectionRef.current = null;
      }
      return;
    }

    const syncKey = JSON.stringify({
      start: activeSelection.start,
      end: activeSelection.end,
      startTime: activeSelection.startTime,
      endTime: activeSelection.endTime,
      resourceId: activeSelection.resourceId,
      selectedIds: [...activeSelection.selectedIds].sort((a, b) => a - b),
      isDayView,
      visibleRangeStart: visibleRange.start.getTime(),
      visibleRangeEnd: visibleRange.end.getTime(),
    });

    if (syncedSelectionRef.current === syncKey) {
      return;
    }

    if (syncedSelectionRef.current !== null) {
      calendar.unselect();
    }

    isSyncingSelectionRef.current = true;

    if (isDayView) {
      const start = formatIsoDate(
        activeSelection.start,
        activeSelection.startTime ?? '00:00',
      );
      const end = formatIsoDate(
        activeSelection.end,
        activeSelection.endTime ?? '23:59',
      );

      calendar.select({
        start,
        end,
        resourceId: activeSelection.resourceId,
        allDay: false,
      });
    } else {
      const exclusiveEnd = formatIsoDate(
        subtractDay(new Date(activeSelection.end), -1),
      );

      calendar.select({
        start: activeSelection.start,
        end: exclusiveEnd,
        resourceId: activeSelection.resourceId,
        allDay: true,
      });
    }

    window.requestAnimationFrame(() => {
      isSyncingSelectionRef.current = false;
    });
    syncedSelectionRef.current = syncKey;
  }, [activeSelection, calendar, isDayView, visibleRange]);

  return {
    isSyncingSelectionRef,
  };
}
